import { useState, useEffect } from "react";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
import { springs } from "@/components/ui/motion";
import { useAuditDetail } from "@/hooks/use-audit-detail";
import { useUpdateAuditTags } from "@/hooks/use-audit-tags";
import { AuditDetailHeader } from "@/components/audits/AuditDetailHeader";
import { AUDIT_DETAIL_TABS } from "@/components/audits/AuditDetailTabs";
import { AuditOverviewTab, SoaTab } from "@/components/audits/overview";
import { ReportSideSheet } from "@/components/audits/overview/ReportSideSheet";
import { AuditTagSelectorSheet } from "@/components/audits/AuditTagSelectorSheet";
import {
  AuditChecklistTab,
  AuditDocumentsTab,
  AuditClausesTab,
  AuditActivityTab,
  AuditFindingsTab,
} from "@/components/audits/tabs";
import type { Id } from "@/packages/types";
import { useChatContext } from "@/stores/chat-context-store";
import { useTranslation } from "@/packages/utils";
import { toAuditId } from "@/lib/route-params";
import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import { api } from "convex/_generated/api";

const auditDetailSearchSchema = z.object({
  tab: z
    .enum([
      "overview",
      "soa",
      "checklist",
      "documents",
      "clauses",
      "findings",
      "activity",
    ])
    .optional()
    .default("overview"),
  checklistItemId: z.string().optional(),
  detailTab: z.string().optional(),
  /** Activity feed filter categories (comma-separated: status,content,people,system) */
  categories: z.string().optional(),
  /** Activity feed search query */
  search: z.string().optional(),
  /** Auto-open the report side sheet when navigating from reports list */
  openReport: z.boolean().optional(),
  createFinding: z.boolean().optional(),
});

export const Route = createFileRoute("/$workspaceSlug/audits/$auditId/")({
  component: AuditDetailRoute,
  validateSearch: auditDetailSearchSchema,
});

function getTabIndex(tab: string): number {
  const idx = (AUDIT_DETAIL_TABS as readonly string[]).indexOf(tab);
  return idx >= 0 ? idx : 0;
}

function AuditDetailRoute() {
  const { t } = useTranslation(["audits", "common"]);
  const { auditId: auditIdParam, workspaceSlug } = Route.useParams();
  const auditId = toAuditId(auditIdParam);
  const navigate = useNavigate();
  const { tab, checklistItemId, detailTab, openReport, createFinding } =
    useSearch({
      from: "/$workspaceSlug/audits/$auditId/",
    });
  const activeTab = tab ?? "overview";

  const [prevIndex, setPrevIndex] = useState(getTabIndex(activeTab));
  const currentIndex = getTabIndex(activeTab);
  const direction = currentIndex >= prevIndex ? 1 : -1;

  useEffect(() => {
    setPrevIndex(currentIndex);
  }, [currentIndex]);

  const { audit, categories, dashboardMetrics, isLoading } =
    useAuditDetail(auditId);
  const updateAuditTags = useUpdateAuditTags();

  // Set ambient + entity chat context for Audit detail page
  const setAmbientContext = useChatContext((s) => s.setAmbientContext);
  const clearAmbientContext = useChatContext((s) => s.clearAmbientContext);
  const addEntity = useChatContext((s) => s.addEntity);
  const removeEntity = useChatContext((s) => s.removeEntity);

  useEffect(() => {
    setAmbientContext({
      page: "audits",
      label: t("audits:detail.contextLabel", {
        name: audit?.displayName ?? t("common:status.loading"),
      }),
    });
    return () => clearAmbientContext();
  }, [audit?.displayName, setAmbientContext, clearAmbientContext, t]);

  const auditEntityId = audit?.id;
  const auditDisplayName = audit?.displayName;

  useEffect(() => {
    if (auditEntityId && auditDisplayName) {
      addEntity({ type: "audit", id: auditEntityId, label: auditDisplayName });
      return () => removeEntity(auditEntityId);
    }
  }, [auditEntityId, auditDisplayName, addEntity, removeEntity]);
  const [showTagSelector, setShowTagSelector] = useState(false);
  const [uploadSheetOpen, setUploadSheetOpen] = useState(false);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [reportSheetOpen, setReportSheetOpen] = useState(false);

  // Query the report for this audit so we can pass reportId to ReportSideSheet
  const { data: auditReport } = useWorkspaceQuery(
    api.report_queries.getByAudit,
    { auditId },
  );

  // Open report sheet when openReport search param is set
  useEffect(() => {
    if (openReport) {
      setReportSheetOpen(true);
    }
  }, [openReport]);

  const handleSaveTags = async (tagIds: string[]) => {
    await updateAuditTags({
      auditId,
      // FIXME: TagSelector passes string[] — cast required until TagSelector provides branded Id<"auditTags">[]
      tagIds: tagIds as Id<"auditTags">[],
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-secondary" />
        <p className="text-sm text-secondary">{t("audits:detail.loading")}</p>
      </div>
    );
  }

  // Not found state
  if (!audit) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-lg text-secondary">{t("audits:detail.notFound")}</p>
        <button
          type="button"
          onClick={() =>
            navigate({
              to: "/$workspaceSlug/audits",
              params: { workspaceSlug },
            })
          }
          className="text-sm text-accent hover:underline"
        >
          {t("audits:detail.backToAudits")}
        </button>
      </div>
    );
  }

  const handleBack = () =>
    navigate({
      to: "/$workspaceSlug/audits",
      params: { workspaceSlug },
    });

  return (
    <motion.div
      className="flex flex-col h-full overflow-hidden p-4 lg:p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={springs.smooth}
    >
      <AuditDetailHeader
        audit={audit}
        onBack={handleBack}
        uploadSheetOpen={uploadSheetOpen}
        onOpenUpload={() => setUploadSheetOpen(true)}
        onCloseUpload={() => setUploadSheetOpen(false)}
        editSheetOpen={editSheetOpen}
        onOpenEdit={() => setEditSheetOpen(true)}
        onCloseEdit={() => setEditSheetOpen(false)}
      />

      <div className="flex-1 overflow-hidden flex flex-col">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeTab}
            className="flex-1 flex flex-col min-h-0"
            initial={{ x: direction * 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: direction * -20, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            {activeTab === "overview" && (
              <AuditOverviewTab
                audit={audit}
                categories={categories ?? []}
                dashboardMetrics={dashboardMetrics}
                onEditTags={() => setShowTagSelector(true)}
                onUploadDocument={() => setUploadSheetOpen(true)}
                onEditAudit={() => setEditSheetOpen(true)}
                onOpenReport={() => setReportSheetOpen(true)}
              />
            )}
            {activeTab === "soa" && <SoaTab auditId={auditId} />}
            {activeTab === "documents" && (
              <AuditDocumentsTab
                auditId={auditId}
                preSelectedAudit={
                  audit
                    ? {
                        id: audit.id,
                        displayName: audit.displayName,
                        code: audit.code,
                      }
                    : undefined
                }
              />
            )}
            {activeTab === "clauses" && <AuditClausesTab auditId={auditId} />}
            {activeTab === "activity" && <AuditActivityTab auditId={auditId} />}
            {activeTab === "checklist" && (
              <AuditChecklistTab
                auditId={auditId}
                initialChecklistItemId={checklistItemId}
                initialDetailTab={detailTab}
              />
            )}
            {activeTab === "findings" && (
              <AuditFindingsTab
                auditId={auditId}
                initialCreateFinding={createFinding}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <AuditTagSelectorSheet
        open={showTagSelector}
        onClose={() => setShowTagSelector(false)}
        auditTags={audit.tags}
        auditName={audit.displayName}
        onSave={handleSaveTags}
      />

      <ReportSideSheet
        reportId={auditReport?._id ?? null}
        auditId={audit.id}
        auditName={audit.displayName}
        auditStatus={audit.status}
        open={reportSheetOpen}
        onClose={() => setReportSheetOpen(false)}
      />
    </motion.div>
  );
}
